import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, ExternalLink, Search } from "lucide-react";
import { toast } from "sonner";

const empty = {
  plate: "", vin: "", brand: "", model: "", color: "",
  insurance: "", expert: "", entry_date: "", repair_date: "",
  client_id: null, notes: "",
};

export default function ExpedientesPage() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [search, setSearch] = useState("");

  const load = async () => {
    try {
      const [v, c] = await Promise.all([api.get("/vehicles"), api.get("/clients")]);
      setItems(v.data); setClients(c.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.plate || !form.vin) { toast.error("Matrícula y VIN son obligatorios"); return; }
    try {
      await api.post("/vehicles", form);
      setOpen(false); setForm(empty); load();
      toast.success("Expediente creado");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!confirm("¿Borrar expediente y todas sus fotos?")) return;
    try { await api.delete(`/vehicles/${id}`); load(); toast.success("Expediente borrado"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const clientName = (id) => clients.find(c => c.id === id)?.name || "—";

  const norm = (s) => (s || "").toString().toLowerCase();
  const filtered = items.filter((v) => {
    if (!search.trim()) return true;
    const n = norm(search);
    return [v.plate, v.vin, v.brand, v.model, v.color, clientName(v.client_id), v.insurance, v.expert]
      .some((f) => norm(f).includes(n));
  });

  return (
    <div data-testid="expedientes-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="section-title">Expedientes</h1>
          <p className="text-sm text-zinc-500 mt-1">Cada coche es un expediente con sus fotos, presupuestos y facturas.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-new-vehicle"><Plus size={16} className="mr-1" />Nuevo expediente</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl">
            <DialogHeader><DialogTitle>Nuevo expediente</DialogTitle></DialogHeader>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Matrícula *</Label><Input data-testid="veh-plate" value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })} /></div>
              <div><Label className="text-xs">VIN *</Label><Input data-testid="veh-vin" value={form.vin} onChange={(e) => setForm({ ...form, vin: e.target.value.toUpperCase() })} /></div>
              <div><Label className="text-xs">Color</Label><Input data-testid="veh-color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} /></div>
              <div><Label className="text-xs">Marca</Label><Input data-testid="veh-brand" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} /></div>
              <div><Label className="text-xs">Modelo</Label><Input data-testid="veh-model" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
              <div>
                <Label className="text-xs">Cliente</Label>
                <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? null : v })}>
                  <SelectTrigger data-testid="veh-client"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Sin cliente —</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Aseguradora</Label><Input value={form.insurance} onChange={(e) => setForm({ ...form, insurance: e.target.value })} /></div>
              <div><Label className="text-xs">Perito</Label><Input value={form.expert} onChange={(e) => setForm({ ...form, expert: e.target.value })} /></div>
              <div></div>
              <div><Label className="text-xs">Fecha entrada</Label><Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} /></div>
              <div><Label className="text-xs">Fecha prevista reparación</Label><Input type="date" value={form.repair_date} onChange={(e) => setForm({ ...form, repair_date: e.target.value })} /></div>
              <div></div>
              <div className="col-span-3"><Label className="text-xs">Observaciones</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button data-testid="btn-save-vehicle" onClick={submit}>Crear</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-4 flex items-center gap-2 max-w-md">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            data-testid="vehicles-search"
            placeholder="Buscar por matrícula, VIN, marca, cliente…"
            className="pl-8"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <span className="text-xs text-zinc-400">{filtered.length} / {items.length}</span>
      </div>

      <div className="page-card overflow-x-auto">
        <table className="tbl" data-testid="vehicles-table">
          <thead><tr><th>Matrícula</th><th>Marca/Modelo</th><th>Color</th><th>Cliente</th><th>Entrada</th><th></th></tr></thead>
          <tbody>
            {filtered.map((v) => (
              <tr key={v.id} data-testid={`vehicle-row-${v.id}`}>
                <td className="font-medium font-mono">{v.plate}</td>
                <td>{v.brand} {v.model}</td>
                <td>{v.color || "—"}</td>
                <td>{clientName(v.client_id)}</td>
                <td>{v.entry_date || "—"}</td>
                <td className="text-right whitespace-nowrap">
                  <Link to={`/expedientes/${v.id}`} className="inline-block px-2 py-1 hover:bg-zinc-100 rounded" data-testid={`open-vehicle-${v.id}`}><ExternalLink size={14} /></Link>
                  <button onClick={() => del(v.id)} className="px-2 py-1 hover:bg-red-50 text-red-600 rounded" data-testid={`del-vehicle-${v.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan="6" className="text-center text-zinc-400 py-10">{items.length === 0 ? "Sin expedientes todavía." : "Sin resultados."}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
